"use client";

// Shared behaviour for the two COMMAND sidebars — the page's left panel and
// the global Talk to Command drawer. Both show the same My agents / Projects /
// Other chats lists, so the two things that govern those lists live here
// rather than being written twice and drifting:
//
//   • how long a list gets before it offers a way out — "Show more" for
//     agents, "View all" (a page) for projects and chats;
//   • drag-to-reorder for agents and projects, with the list previewing the
//     order the drop would produce.
//
// The preview runs through the same pure helper the store commits with, so
// what you see mid-drag and what you get on release can never disagree.

import {
  useState,
  type Dispatch,
  type DragEvent,
  type SetStateAction,
} from "react";

import { reorderByDrop, useCommandPrototype } from "../prototype/store";
import type { ProtoCommandAgent, ProtoProject } from "../prototype/types";
import {
  agentRowDropHandlers,
  projectRowDropHandlers,
  reorderListDropHandlers,
  setAgentDragData,
  setProjectDragData,
} from "./chat-dnd";

/** How much of each list a sidebar shows before offering the way out. */
export const SIDEBAR_LIMITS = {
  /** My agents: this many, then "Show more" reveals another `agentStep`. */
  agents: 10,
  agentStep: 5,
  /** Projects: this many, then "Load more" reveals another `projectStep`
   *  ("View all" sits by the section title and opens the projects page). */
  projects: 5,
  projectStep: 5,
  /** Other chats: this many, then "View all" opens the chats page. */
  chats: 15,
} as const;

export const CHATS_PAGE_PATH = "/workforce/command-team/chats";
export const AGENTS_PAGE_PATH = "/workforce/command-team/agents";
export const PROJECTS_PAGE_PATH = "/workforce/command-team/projects";
export const SCHEDULED_PAGE_PATH = "/workforce/command-team/scheduled";

/**
 * One list's drag state. Kept as its own closure per list so an agent drag and
 * a project drag can never be mistaken for one another.
 */
function useReorderable<T extends { id: string }>(
  items: T[],
  commit: (itemId: string, targetItemId: string) => void
) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);

  const ordered =
    draggingId && targetId ? reorderByDrop(items, draggingId, targetId) ?? items : items;

  const end = () => {
    setDraggingId(null);
    setTargetId(null);
  };

  /** Hovering the dragged row itself (it moved under the cursor) keeps the preview. */
  const dragOver = (candidateId: string) => {
    if (candidateId === draggingId) return;
    setTargetId((current) => (current === candidateId ? current : candidateId));
  };

  /**
   * Commit the previewed order. Deliberately uses the tracked target rather
   * than whatever row caught the drop — with the list re-laid-out under the
   * cursor, that row is often the dragged one.
   */
  const drop = (itemId: string) => {
    if (targetId && targetId !== itemId) commit(itemId, targetId);
    end();
  };

  return { ordered, draggingId, setDraggingId, dragOver, drop, end };
}

export function useSidebarLists(options: {
  /** panel-level chat drop-target highlight, owned by the calling sidebar */
  setDragOver: Dispatch<SetStateAction<string | null>>;
  onDropChatIntoProject: (sessionId: string, projectId: string) => void;
}) {
  const { state, actions } = useCommandPrototype();

  const agentDrag = useReorderable(state.agents, actions.reorderAgent);
  const projectDrag = useReorderable(state.projects, actions.reorderProject);

  // Agents grow in place rather than moving to a page — the list is a picker,
  // and being sent elsewhere to choose who answers would break the thought.
  const [agentsShown, setAgentsShown] = useState<number>(SIDEBAR_LIMITS.agents);

  // Only the agents marked "shown in sidebar" appear, but the reorder runs on
  // the full array — see reorderByDrop.
  const shownAgents = agentDrag.ordered.filter((agent) => agent.isActive);
  const visibleAgents = shownAgents.slice(0, agentsShown);
  // Projects grow in place too — "Load more" reveals another step.
  const [projectsShown, setProjectsShown] = useState<number>(SIDEBAR_LIMITS.projects);

  const visibleProjects = projectDrag.ordered.slice(0, projectsShown);

  return {
    agents: {
      visible: visibleAgents as ProtoCommandAgent[],
      total: shownAgents.length,
      /** how many more a "Show more" would reveal (0 → no button) */
      remaining: Math.max(0, shownAgents.length - visibleAgents.length),
      showMore: () => setAgentsShown((current) => current + SIDEBAR_LIMITS.agentStep),
      draggingId: agentDrag.draggingId,
      rowProps: (agentId: string) => ({
        draggable: true,
        onDragStart: (event: DragEvent) => {
          setAgentDragData(event, agentId);
          agentDrag.setDraggingId(agentId);
        },
        onDragEnd: agentDrag.end,
        ...agentRowDropHandlers({
          agentId,
          onAgentDragOver: agentDrag.dragOver,
          onDropAgent: agentDrag.drop,
        }),
      }),
      listProps: reorderListDropHandlers({ kind: "agent", onDropItem: agentDrag.drop }),
    },
    projects: {
      visible: visibleProjects as ProtoProject[],
      total: projectDrag.ordered.length,
      overflowing: projectDrag.ordered.length > SIDEBAR_LIMITS.projects,
      /** how many more a "Load more" would reveal (0 → no button) */
      remaining: Math.max(0, projectDrag.ordered.length - visibleProjects.length),
      loadMore: () =>
        setProjectsShown((current) => current + SIDEBAR_LIMITS.projectStep),
      /** grown past the base cap — "Show less" folds it back to five */
      expanded: projectsShown > SIDEBAR_LIMITS.projects,
      showLess: () => setProjectsShown(SIDEBAR_LIMITS.projects),
      draggingId: projectDrag.draggingId,
      rowProps: (projectId: string) => ({
        draggable: true,
        onDragStart: (event: DragEvent) => {
          setProjectDragData(event, projectId);
          projectDrag.setDraggingId(projectId);
        },
        onDragEnd: projectDrag.end,
        ...projectRowDropHandlers({
          projectId,
          setDragOver: options.setDragOver,
          onDropChat: (sessionId) => options.onDropChatIntoProject(sessionId, projectId),
          onProjectDragOver: projectDrag.dragOver,
          onDropProject: projectDrag.drop,
        }),
      }),
      listProps: reorderListDropHandlers({ kind: "project", onDropItem: projectDrag.drop }),
    },
  };
}
